/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { cva, VariantProps } from 'class-variance-authority'
import React, { HTMLAttributes } from 'react'

import { cn } from '@/utils/css'

/** Variants for the typography component. */
const typographyVariants = cva('', {
  variants: {
    variant: {
      xxxxxl: 'text-9xl',
      xxxxl: 'text-8xl',
      xxxl: 'text-7xl',
      xxl: 'text-6xl',
      xl: 'text-5xl',
      h1: 'text-4xl font-bold md:text-5xl',
      h2: 'text-3xl font-bold md:text-4xl',
      h3: 'text-2xl font-bold md:text-3xl',
      h4: 'text-xl font-semibold md:text-2xl',
      h5: 'text-lg font-semibold md:text-xl',
      h6: 'text-base font-semibold md:text-lg',
      p: 'text-base',
      dd: 'text-base',
      dt: 'text-base',
      small: 'text-xs sm:text-sm',
    },
    colour: {
      default: 'text-gray-900',
      muted: 'text-gray-600',
      inverted: 'text-white',
      primary: 'text-primary-500',
      secondary: 'text-secondary-500',
      'white-overlay': 'text-shadow-[2px_2px_0_rgba(0,0,0,0.3)] text-white',
    },
    weight: {
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
  },
  defaultVariants: {
    variant: 'p',
    colour: 'default',
    weight: 'normal',
    align: 'left',
  },
})

/** A description of available properties for this component. */
type TypographyProps = HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof typographyVariants> & {
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'dd' | 'dt'
  }

/**
 * A customisable typography component that can be used for various text elements.
 */
const Typography: React.FC<TypographyProps> = ({ className, variant, colour, weight, as, align, ...props }) => {
  const Component = as || 'p'
  return <Component className={cn(typographyVariants({ variant, colour, weight, className, align }))} {...props} />
}

export { Typography }
